import { Bounds, XYLocation } from "@nens/deck-gl";

import { createViz } from "./createViz";
import { cropped } from "./cropped";
import { enormous } from "./enormous";

// *** Explenation ***
// The code below is somewhat complicated, so let me explain.

// *** Basic Viz setup ***
// For each zoom level defined in the translateZoom function, we create a Viz. A Viz contains two canvases: one for the DEM itself and one for the NS, meaning the encoded hillshade.
// Both of these canvases have the pixel size needed to cover the entire extent. We retrieve the tiles within the user's viewport and their images from the backend and draw them onto the canvas.
// We then keep track of which tiles are already drawn so we don't query the same tiles multiple times.
// We then send the canvasses away to be converted to a hillshade image and drawn to the map. If ther is nothing to redraw because nothing new is rendered it returns null

// *** Activate or deactivate NS ***
// In the getRegion function, you can set the NS as active or inactive. We have this setting because of the 2D flow in the threedi-live-site, we always need to get the current DEM to hide the 2D flow visualization where the DEM is unknown.

// *** Cropped canvas ***
// A Viz can also optionally contain two cropped canvases. We have this feature because if the canvas is too large and you try to draw the hillshade, the whole map can break.
// So if the canvas is too large, instead of sending the giant canvas, we copy the part that is in the user's viewport onto the cropped canvas and send that to the user.

// *** Giant canvas ***
// If a canvas is larger then 25 mil pixels. You can't render anything to it. This is a limitation of javascript itself. We fix this by splitting the canvas up in 4 canvasses and drawing to those. Then copying that to the cropped canvas.
// This works fine for the sizes we use now. Maybe if we some day get even bigger canvasses this will need fixing.

// *** Enormous canvas ***
// If a canvas is larger then 100 mil pixels. Even splitting it in 4 wont work. So we just request all the needed tiles every time the user moves
// This only happens on canvasses that are like the size of a country

export type ImagePromise = {
  imagePromise: Promise<HTMLImageElement>;
  x: number;
  y: number;
  imageType: "NS" | "DEM";
};

export type XyInfo = { lowest?: [number, number]; highest?: [number, number] };

type VizBasis = {
  zoomLevel: number;
  newBounds: Bounds;
  tLong: number;
  tLat: number;
  tileAmountX: number;
  tileAmountY: number;
  visualizedXYNS: XYLocation[];
  visualizedXYDEM: XYLocation[];
};

interface NormalTypes extends VizBasis {
  canvasNS: OffscreenCanvas;
  canvasDEM: OffscreenCanvas;
  ctxNS: OffscreenCanvasRenderingContext2D;
  ctxDem: OffscreenCanvasRenderingContext2D;
}

interface CroppedTypes {
  croppedBounds: Bounds;
  croppedCanvasNS: OffscreenCanvas;
  croppedCanvasDEM: OffscreenCanvas;
  croppedCtxNS: OffscreenCanvasRenderingContext2D;
  croppedCtxDem: OffscreenCanvasRenderingContext2D;
}

interface NormalViz extends NormalTypes {
  type: "normal";
}

export interface CroppedViz extends NormalTypes, CroppedTypes {
  type: "cropped";
}

export interface GiantViz extends VizBasis, CroppedTypes {
  type: "giant";
  canvasNS: OffscreenCanvas[];
  canvasDEM: OffscreenCanvas[];
  ctxNS: OffscreenCanvasRenderingContext2D[];
  ctxDem: OffscreenCanvasRenderingContext2D[];
}

export interface EnormousViz extends CroppedTypes {
  type: "enormous";
  zoomLevel: number;
}

export type Viz = NormalViz | CroppedViz | GiantViz | EnormousViz;

const savedVizzes: Viz[] = [];
let lastZoomLevel: number | null = null;

export const getRegion = async (
  extentCoordinates: number[][],
  mapBounds: Bounds,
  terrainLayerUrl: string,
  zoom: number | null,
  nsActive?: boolean
): Promise<
  { imageNS: OffscreenCanvas; imageDEM: OffscreenCanvas; newBounds: Bounds } | undefined | null
> => {
  if (zoom === null) return;
  const viz =
    savedVizzes.find((viz) => zoom !== null && viz.zoomLevel === translateZoom(zoom)) ||
    createViz(extentCoordinates, translateZoom(zoom), savedVizzes);
  if (viz.type === "enormous")
    return await enormous(viz, mapBounds, terrainLayerUrl, Math.floor(zoom), nsActive);
  zoom = null;
  const {
    zoomLevel,
    canvasNS,
    canvasDEM,
    ctxNS,
    ctxDem,
    newBounds,
    tLong,
    tLat,
    tileAmountX,
    tileAmountY,
    visualizedXYNS,
    visualizedXYDEM,
    type,
  } = viz;

  const imagePromises: ImagePromise[] = [];

  const tLatMap = Math.floor(lat2tile(mapBounds[1], zoomLevel));
  const tLatEndMap = Math.floor(lat2tile(mapBounds[3], zoomLevel));

  const tLongMap = Math.floor(long2tile(mapBounds[0], zoomLevel));
  const tLongEndMap = Math.floor(long2tile(mapBounds[2], zoomLevel));

  // This info is used to get the needed render bounds if we are using the cropped canvas
  const xyInfo: XyInfo = {};
  // Loop through tiles
  for (let x = 0; x < tileAmountX; x++) {
    const _tLong = tLong + (x - 1);
    for (let y = 0; y < tileAmountY; y++) {
      const _tLat = tLat + (y - 1);
      // Check if tile is in viewport
      if (tLongMap <= _tLong && _tLong <= tLongEndMap && tLatMap <= _tLat && _tLat <= tLatEndMap) {
        if (!xyInfo.lowest) xyInfo.lowest = [x, y];
        xyInfo.highest = [x + 1, y + 1];

        const url = terrainLayerUrl
          .replace("{z}", zoomLevel + "")
          .replace("{x}", _tLong + "")
          .replace("{y}", _tLat + "");

        // Check if NS is active and NS tile is already visualized
        if (nsActive && !visualizedXYNS.find((xy) => xy[0] === x && xy[1] === y)) {
          visualizedXYNS.push([x, y]);

          imagePromises.push({
            imagePromise: loadImage(url + "&colormap=ns"),
            x,
            y,
            imageType: "NS",
          });
        }

        // Check if DEM tile is already visualized
        if (!visualizedXYDEM.find((xy) => xy[0] === x && xy[1] === y)) {
          visualizedXYDEM.push([x, y]);

          imagePromises.push({ imagePromise: loadImage(url), x, y, imageType: "DEM" });
        }
      }
    }
  }

  const zoomLevelChanged = !lastZoomLevel || lastZoomLevel !== zoomLevel;
  lastZoomLevel = zoomLevel;
  const redraw = zoomLevelChanged || !!imagePromises.length;

  const images = await Promise.all(imagePromises.map(({ imagePromise }) => imagePromise));

  // Draw new tiles to canvas
  images.forEach((image, index) => {
    const { imageType } = imagePromises[index];
    let { x, y } = imagePromises[index];

    if (type === "giant") {
      const ctxArray = imageType === "NS" ? ctxNS : ctxDem;
      let index = 0;
      const halfTileAmountX = Math.floor(tileAmountX / 2);
      const halfTileAmountY = Math.floor(tileAmountY / 2);
      if (x >= halfTileAmountX) {
        x -= halfTileAmountX;
        index += 1;
      }
      if (y >= halfTileAmountY) {
        y -= halfTileAmountY;
        index += 2;
      }
      return ctxArray[index]?.drawImage(image, x * 256, y * 256);
    }
    (imageType === "NS" ? ctxNS : ctxDem)?.drawImage(image, x * 256, y * 256);
  });

  // Cropped/giant canvas logic
  if (viz.type === "cropped" || type === "giant") {
    if (!xyInfo.lowest || !xyInfo.highest) return;

    const { croppedBounds } = viz;

    const newBoundsInsideOld =
      croppedBounds &&
      mapBounds[0] >= croppedBounds[0] &&
      mapBounds[1] <= croppedBounds[1] &&
      mapBounds[2] <= croppedBounds[2] &&
      mapBounds[3] >= croppedBounds[3];

    if (!redraw && newBoundsInsideOld) return null;

    // Get the bounds we will render
    viz.croppedBounds = [
      tile2long(tLong + xyInfo.lowest[0] - 1, zoomLevel),
      tile2lat(tLat + xyInfo.lowest[1] - 1, zoomLevel),
      tile2long(tLong + xyInfo.highest[0] - 1, zoomLevel),
      tile2lat(tLat + xyInfo.highest[1] - 1, zoomLevel),
    ] as Bounds;

    return cropped(viz, xyInfo);
  }
  if (redraw) return { imageNS: canvasNS, imageDEM: canvasDEM, newBounds };
};

export const loadImage = (url: string) =>
  new Promise<HTMLImageElement>((resolve, error) => {
    const img = new Image();
    img.onload = () => {
      resolve(img);
    };
    img.onerror = error;
    img.crossOrigin = "anonymous";
    img.src = url;
  });

// Hard removes all the vizzes. Needs to be called if a new simulation is started
export const removeDemVizzes = () => {
  while (savedVizzes.length) {
    savedVizzes.pop();
  }
};

// Soft resets the dem vizzes. Doesnt delete the already created canvasses and bounds etc. Just makes sure all the tiles are refetched. Used when rescaling the DEM
export const resetDemVizzes = () => {
  savedVizzes.forEach((viz) => {
    if ("visualizedXYDEM" in viz) viz.visualizedXYDEM = [];
  });
};

// Try to keep the amount of different zoom levels to a minimum. Every zoom level is another canvas that needs to be filled
export const translateZoom = (zoom: number) => {
  if (zoom >= 15) return 17;
  return 14;
};

export const lat2tile = (l: number, zoom: number) =>
  ((1 - Math.log(Math.tan((l * Math.PI) / 180) + 1 / Math.cos((l * Math.PI) / 180)) / Math.PI) /
    2) *
  Math.pow(2, zoom);

export const long2tile = (l: number, zoom: number) => ((l + 180) / 360) * Math.pow(2, zoom);

export const tile2long = (x: number, zoom: number) => (x / Math.pow(2, zoom)) * 360 - 180;

export const tile2lat = (y: number, zoom: number) =>
  (Math.atan(Math.sinh(Math.PI * (1 - 2 * (y / Math.pow(2, zoom))))) * 180) / Math.PI;
